import ImageUploadField from '../common/ImageUploadField';
import ProductVariantsEditor from './ProductVariantsEditor';
import { appendFormValue, appendFiles } from '../../utils/formData';

export default function ProductForm({ form, onChange, categories = [] }) {
  const set = (field, value) => onChange({ ...form, [field]: value });

  const setArrayField = (field, value) => {
    const arr = value.split(',').map((s) => s.trim()).filter(Boolean);
    set(field, arr);
  };

  return (
    <div className="form-grid">
      <div className="form-group full">
        <label>Name *</label>
        <input
          className="input"
          value={form.name || ''}
          onChange={(e) => set('name', e.target.value)}
          required
        />
      </div>
      <div className="form-group">
        <label>Price (₹) *</label>
        <input
          type="number"
          className="input"
          min="0"
          step="0.01"
          value={form.price ?? ''}
          onChange={(e) => set('price', parseFloat(e.target.value) || 0)}
          required
        />
      </div>
      <div className="form-group">
        <label>Original Price (₹)</label>
        <input
          type="number"
          className="input"
          min="0"
          step="0.01"
          value={form.originalPrice ?? ''}
          onChange={(e) => set('originalPrice', parseFloat(e.target.value) || '')}
        />
      </div>
      <div className="form-group">
        <label>Category</label>
        <select
          className="input"
          value={form.category || ''}
          onChange={(e) => set('category', e.target.value)}
           required
        >
          <option value="">Select category</option>
          {categories.map((cat) => (
            <option key={cat._id} value={cat._id}>
              {cat.name}
            </option>
          ))}
        </select>
      </div>
      <div className="form-group">
        <label>Slug</label>
        <input
          className="input"
          value={form.slug || ''}
          onChange={(e) => set('slug', e.target.value)}
          placeholder="Auto-generated if empty"
        />
      </div>
      <div className="form-group">
        <label>Badge</label>
        <input
          className="input"
          value={form.badge || ''}
          onChange={(e) => set('badge', e.target.value)}
        />
      </div>
      <div className="form-group full">
        <label>Short Description</label>
        <input
          className="input"
          value={form.shortDescription || ''}
          onChange={(e) => set('shortDescription', e.target.value)}
        />
      </div>
      <div className="form-group full">
        <label>Description</label>
        <textarea
          className="input textarea"
          rows={3}
          value={form.description || ''}
          onChange={(e) => set('description', e.target.value)}
        />
      </div>

      <ImageUploadField
        label="Product Images"
        multiple
        maxFiles={10}
        existingUrls={form.existingImages || []}
        onExistingChange={(urls) => set('existingImages', urls)}
        files={form.newImageFiles || []}
        onFilesChange={(files) => set('newImageFiles', files)}
      />

      <div className="form-group full">
        <label>Benefits (comma separated)</label>
        <input
          className="input"
          value={(form.benefits || []).join(', ')}
          onChange={(e) => setArrayField('benefits', e.target.value)}
        />
      </div>
      <div className="form-group checkbox-group">
        <label>
          <input
            type="checkbox"
            checked={!!form.inStock}
            onChange={(e) => set('inStock', e.target.checked)}
          />
          In Stock
        </label>
      </div>
      <div className="form-group checkbox-group">
        <label>
          <input
            type="checkbox"
            checked={!!form.featured}
            onChange={(e) => set('featured', e.target.checked)}
          />
          Featured
        </label>
      </div>

      <ProductVariantsEditor
        variants={form.variants || []}
        onChange={(variants) => set('variants', variants)}
      />
    </div>
  );
}

export const emptyProductForm = {
  name: '',
  price: '',
  originalPrice: '',
  category: '',
  slug: '',
  badge: '',
  shortDescription: '',
  description: '',
  existingImages: [],
  newImageFiles: [],
  benefits: [],
  inStock: true,
  featured: false,
  variants: [],
};

export function productToForm(product) {
  if (!product) return { ...emptyProductForm };
  return {
    name: product.name || '',
    price: product.price ?? '',
    originalPrice: product.originalPrice ?? '',
    category: product.category?._id || product.category || '',
    slug: product.slug || '',
    badge: product.badge || '',
    shortDescription: product.shortDescription || '',
    description: product.description || '',
    existingImages: product.images || [],
    newImageFiles: [],
    benefits: product.benefits || [],
    inStock: product.inStock !== false,
    featured: !!product.featured,
    variants: (product.variants || []).map((v) => ({
      _id: v._id,
      sku: v.sku || '',
      label: v.label || '',
      price: v.price ?? '',
      originalPrice: v.originalPrice ?? '',
      stock: v.stock ?? 0,
      images: v.images || [],
      attributes: v.attributes || [],
      inStock: v.inStock !== false,
    })),
  };
}

export function formToProductFormData(form) {
  const formData = new FormData();

  appendFormValue(formData, 'name', form.name);
  appendFormValue(formData, 'price', form.price);
  appendFormValue(formData, 'originalPrice', form.originalPrice);
  appendFormValue(formData, 'category', form.category);
  appendFormValue(formData, 'slug', form.slug);
  appendFormValue(formData, 'badge', form.badge);
  appendFormValue(formData, 'shortDescription', form.shortDescription);
  appendFormValue(formData, 'description', form.description);
  appendFormValue(formData, 'benefits', form.benefits || []);
  appendFormValue(formData, 'inStock', form.inStock);
  appendFormValue(formData, 'featured', form.featured);
  appendFormValue(formData, 'existingImages', form.existingImages || []);
  appendFiles(formData, 'images', form.newImageFiles || []);

  if (form.variants?.length) {
    const variantsPayload = form.variants.map((v) => ({
      ...(v._id ? { _id: v._id } : {}),
      sku: v.sku,
      label: v.label,
      price: Number(v.price),
      originalPrice: v.originalPrice !== '' ? Number(v.originalPrice) : undefined,
      stock: Number(v.stock) || 0,
      images: v.images || [],
      attributes: (v.attributes || []).filter((a) => a.name && a.value),
      inStock: v.inStock !== false,
    }));
    appendFormValue(formData, 'variants', variantsPayload);
    appendFormValue(formData, 'hasVariants', true);
  } else {
    appendFormValue(formData, 'hasVariants', false);
    appendFormValue(formData, 'variants', []);
  }

  return formData;
}
